import _, {difference, find, union, uniq} from 'lodash';

import {
  getRegexpMatches,
  isValidEmailAddress,
  randomString,
} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {
  CommentModel,
  CommentStatusesEnum,
  HighlightMessageAnnotationCommentModel,
  RHighlightMessageAnnotationComment,
} from '../../models/api/commentModel';
import {CommentReactionModel} from '../../models/api/commentReactionModel';
import {ConversationTeamStatusesEnum} from '../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../models/api/conversationModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {AppState} from '../../store/appState';
import {ResourceMap} from '../../store/appTypes';
import {getCanInviteVisitors, getIsInTrial} from '../../store/data/company/companySelectors';
import {findMessageRecipients} from '../../store/data/contacts/recipientsSelectors';
import {makeGetConversationInboxes} from '../../store/data/conversations/conversationsSelectors';
import {getMessages} from '../../store/data/dataSelectors';
import {makeGetConversationParticipants} from '../../store/data/teammates/mentionTeammatesSelectors';
import {getTeammates, getUserId} from '../../store/data/teammates/teammatesSelectors';
import {ViewerPanelId} from '../../store/panels/panelStateBase';
import {findTeammateIdsWithInboxAccess, isTeammateActive, isTracker} from '../modelHelpers';
import {isInternalConversation} from './conversationHelpers';

/*
 * New comment.
 */

interface BuildDraftCommentOptions {
  uid?: string;
  urlBuilder: (uid: string) => string;
  authorId: number;
  conversationId?: number;
  lastPanelId?: ViewerPanelId;
  text?: string;
  topic?: string;
  teammateIds?: ReadonlyArray<number>;
}

/** Create a new blank comment. */
export function buildDraftComment(options: BuildDraftCommentOptions): CommentModel {
  const {uid, urlBuilder, authorId, conversationId, lastPanelId, text, topic, teammateIds} = options;

  const newCommentUid = uid || randomString();
  return {
    uid: newCommentUid,
    url: urlBuilder(newCommentUid),
    status: CommentStatusesEnum.DRAFT,
    version: '0',
    authorId,
    teammateIds,
    conversationId: conversationId || 0,
    conversationRef: {
      isLastComment: false,
      conversationId: conversationId || 0,
    },
    text: text || '',
    topic,
    sentAt: 0,
    createdAt: Date.now(),
    updatedAt: 0,
    attachments: [],
    isSaving: false,
    lastPanelId,
    reactions: [],
    unfurls: [],
  };
}

/*
 * Mentions.
 */

const mentionMiddleCharacters = "[\\w!#$%&@'*+\\-\\/=?^_`{|}~.]";
const mentionAliasOrEmail = `\\w(?:${mentionMiddleCharacters}*\\w)?`;

const partialMentionRegexp = new RegExp(`(?:^|\\W)(@(${mentionAliasOrEmail})?)$`, 'i');
const mentionsRegexp = new RegExp(`(^|\\W)(@${mentionAliasOrEmail})`, 'i');
const mentionsRegexpGlobal = new RegExp(mentionsRegexp.source, 'gi');

/** Check whether the provided string is a valid start of a mention. */
export function matchPartialMention(text: string) {
  const partialMentionMatch = text.match(partialMentionRegexp);
  if (!partialMentionMatch) {
    return null;
  }

  return partialMentionMatch[1];
}

type MentionTransformer<T> = (key: string, mention: string) => T;

/** Transform mentions in a text using the provided transformer. */
export function transformMentions<T>(text: string, transformer: MentionTransformer<T>) {
  const textParts = text.split(mentionsRegexp);
  return textParts.map((part, index) => {
    // The regexp has two groups. We're interested in the 2nd one.
    if (index % 3 < 2) {
      return part;
    }

    // Otherwise, render in a component.
    return transformer(index.toString(), part);
  });
}

/** Find the mentioned aliases in the provided text. */
export function findMentionedAliases(text: string) {
  const matches = getRegexpMatches(mentionsRegexpGlobal, text);
  return matches.map((m) => m[2].slice(1).toLowerCase());
}

export function keyMentionableTeammatesByAlias(userId: number, teammates: ResourceMap<TeammateModel>) {
  return _(teammates)
    .values()
    .compact()
    .filter((teammate) => teammate.id !== userId && isTeammateActive(teammate))
    .keyBy((teammate) => teammate.alias)
    .value();
}

export function findMentionedTeammatesForAliases(
  userId: number,
  teammates: ResourceMap<TeammateModel>,
  mentionedAliases: ReadonlyArray<string>,
) {
  const mentionableTeammates = keyMentionableTeammatesByAlias(userId, teammates);

  return _(mentionedAliases)
    .map((alias) => mentionableTeammates[alias])
    .compact()
    .value();
}

export function findMentionedTeammates(userId: number, teammates: ResourceMap<TeammateModel>, text: string) {
  return findMentionedTeammatesForAliases(userId, teammates, findMentionedAliases(text));
}

export function findMentionedTeammateIds(
  userId: number,
  teammates: ResourceMap<TeammateModel>,
  text: string,
) {
  return findMentionedTeammates(userId, teammates, text).map((teammate) => teammate.id);
}

/** Find the mentioned teammates without access to the comment's conversation. */
function findMentionedTeammatesWithoutAccess(
  state: AppState,
  conversation: ConversationModel,
  mentionedTeammates: ReadonlyArray<TeammateModel>,
) {
  // Find all the teammates with access.
  const teammatesWithAccess = findTeammatesWithAccess(state, conversation);

  // Return the difference.
  return difference(mentionedTeammates, teammatesWithAccess);
}

/** Find the teammates with access to a conversation. */
export function findTeammatesWithAccess(state: AppState, conversation: ConversationModel) {
  const teammates = getTeammates(state);
  const getConversationInboxes = makeGetConversationInboxes(() => conversation);
  const conversationInboxes = getConversationInboxes(state, {});

  // Find all the teammates with access to the conversation's inboxes.
  const inboxParticipantsIds = _(conversationInboxes)
    .map((i) => findTeammateIdsWithInboxAccess(teammates, i))
    .flatten()
    .value();

  // Find all the teammates with direct access to the conversation.
  const conversationTrackerIds = _(conversation.trackers)
    .values()
    .compact()
    .filter(isTracker)
    .map((t) => t.teammateId)
    .value();

  // Combine, deduplicate, and map to teammates.
  return _(inboxParticipantsIds)
    .concat(conversationTrackerIds)
    .uniq()
    .map((id) => teammates[id])
    .compact()
    .filter((t) => isTeammateActive(t))
    .value();
}

function findMentionedEmails(
  state: AppState,
  conversation: ConversationModel,
  mentionedAliases: ReadonlyArray<string>,
) {
  // Find all the teammates with access.
  const teammatesWithAccess = findTeammatesWithAccess(state, conversation);
  const teammatesWithAccessEmails = teammatesWithAccess.map((t) => t.email);

  // Filter the aliases by valid emails that don't have access.
  return mentionedAliases.filter((alias) => {
    const email = alias.toLowerCase();
    return isValidEmailAddress(email) && !teammatesWithAccessEmails.includes(email);
  });
}

export function isValidMention(
  mentionableAliases: ReadonlySet<string>,
  mention: string,
  ignoreEmails?: boolean,
) {
  if (!mention.startsWith('@')) {
    return false;
  }

  const lowerCaseMention = mention.slice(1).toLowerCase();
  if (mentionableAliases.has(lowerCaseMention)) {
    return true;
  }

  if (!ignoreEmails && isValidEmailAddress(lowerCaseMention)) {
    return true;
  }

  return false;
}

/*
 * Annotations and highlights.
 */

export function isHighlightComment(comment: CommentModel): comment is HighlightMessageAnnotationCommentModel {
  return RHighlightMessageAnnotationComment.is(comment) && !comment.text && comment.attachments.length === 0;
}

/*
 * Send warnings.
 */

export enum CommentWarningTypesEnum {
  MENTION_ALL = 'MENTION_ALL',
  MENTION_NO_ACCESS = 'MENTION_NO_ACCESS',
  MENTION_VISITOR = 'MENTION_VISITOR',
  MENTION_VISITOR_IN_TRIAL = 'MENTION_VISITOR_IN_TRIAL',
  NO_TOPIC = 'NO_TOPIC',
}

interface MentionAllCommentWarning {
  type: CommentWarningTypesEnum.MENTION_ALL;
  teammatesCount: number;
}

interface MentionWithoutAccessCommentWarning {
  type: CommentWarningTypesEnum.MENTION_NO_ACCESS;
  teammates: ReadonlyArray<TeammateModel>;
}

interface MentionVisitorWarning {
  type: CommentWarningTypesEnum.MENTION_VISITOR;
  emails: ReadonlyArray<string>;
}

interface MentionVisitorInTrialWarning {
  type: CommentWarningTypesEnum.MENTION_VISITOR_IN_TRIAL;
  emails: ReadonlyArray<string>;
}

interface CommentWithoutTopicWarning {
  type: CommentWarningTypesEnum.NO_TOPIC;
}

export type CommentWarning =
  | MentionAllCommentWarning
  | MentionWithoutAccessCommentWarning
  | MentionVisitorWarning
  | MentionVisitorInTrialWarning
  | CommentWithoutTopicWarning;

export function findSendWarnings(
  state: AppState,
  conversation: ConversationModel,
  comment: CommentModel,
  mentionedAliases: ReadonlyArray<string>,
  mentionedTeammates: ReadonlyArray<TeammateModel>,
): ReadonlyArray<CommentWarning> {
  const warnings: Array<CommentWarning> = [];
  const userId = getUserId(state);

  // @all mention warning.
  if (mentionedAliases.includes('all')) {
    const withAccessExceptUser = findTeammatesWithAccess(state, conversation).filter((t) => t.id !== userId);
    warnings.push({
      type: CommentWarningTypesEnum.MENTION_ALL,
      teammatesCount: withAccessExceptUser.length,
    });
  }

  // Teammates without access warning.
  const withoutAccess = findMentionedTeammatesWithoutAccess(state, conversation, mentionedTeammates);
  const invitedTeammateIds = comment.teammateIds || [];
  const withoutAccessAfterInvite = uniq(withoutAccess.filter((t) => !invitedTeammateIds.includes(t.id)));

  if (withoutAccessAfterInvite.length > 0) {
    warnings.push({
      type: CommentWarningTypesEnum.MENTION_NO_ACCESS,
      teammates: withoutAccessAfterInvite,
    });
  }

  // Visitor warning.
  const canInviteVisitors = getCanInviteVisitors(state);
  const mentionedEmails = findMentionedEmails(state, conversation, mentionedAliases);
  if (canInviteVisitors && mentionedEmails.length > 0) {
    const emailsWithoutAccess = findMentionedEmailsWithoutAccess(state, conversation, mentionedEmails);
    // If we are mentioning visitors that are not already recipients, show a warning.
    if (emailsWithoutAccess.length > 0) {
      warnings.push({
        type: CommentWarningTypesEnum.MENTION_VISITOR,
        emails: emailsWithoutAccess,
      });
    }
  }

  // Warning for attempts to @mention email addresses during trial. We disable visitors during trial for spam-prevention.
  const isInTrial = getIsInTrial(state);
  if (isInTrial && mentionedEmails.length > 0) {
    warnings.push({
      type: CommentWarningTypesEnum.MENTION_VISITOR_IN_TRIAL,
      emails: mentionedEmails,
    });
  }

  // First discussion comment without topic.
  const isInternal = isInternalConversation(conversation);
  const isNewInternal = isInternal && conversation.teamStatus === ConversationTeamStatusesEnum.INVISIBLE;
  if (isNewInternal && !comment.topic) {
    warnings.push({
      type: CommentWarningTypesEnum.NO_TOPIC,
    });
  }

  return warnings;
}

/*
 * Helpers.
 */

export function computeHasAnnotation(comment: CommentModel) {
  return Boolean(comment.annotation);
}

export function computeHasAttachments(comment: CommentModel) {
  return comment.attachments.length > 0;
}

// Returns the mentioned emails that are not already recipients or participants.
function findMentionedEmailsWithoutAccess(
  state: AppState,
  conversation: ConversationModel,
  mentionedEmails: ReadonlyArray<string>,
) {
  // Find all the emails with access.
  const emailsWithAccess = union(
    findLastMessageRecipientEmails(state, conversation),
    findConversationParticipantEmails(state, conversation),
  );

  // Return the difference.
  return difference(mentionedEmails, emailsWithAccess);
}

function findLastMessageRecipientEmails(
  state: AppState,
  conversation: ConversationModel,
): ReadonlyArray<string> {
  // conversation.lastMessage is incomplete and only has uid. Use the uid to access the full last message model.
  const lastMessageUid = conversation.lastMessage && conversation.lastMessage.uid;

  // If there is no uid for the last message then don't return any emails.
  if (!lastMessageUid) {
    return [];
  }

  const messages = getMessages(state);
  const lastMessage = messages[lastMessageUid];

  // If the last message is not found then don't return any emails.
  if (!lastMessage) {
    return [];
  }

  // Get the recipients of the last message.
  return findMessageRecipients(state, conversation.namespace, lastMessage).map((t) => t.contact.handle);
}

function findConversationParticipantEmails(
  state: AppState,
  conversation: ConversationModel,
): ReadonlyArray<string> {
  const teammates = makeGetConversationParticipants<void>(() => conversation)(state);
  return teammates.map((teammate) => teammate.email);
}

/*
 * Reactions.
 */

export function teammateHasReactionOnComment(
  comment: CommentModel,
  teammateId: number,
  emojiString: string,
): boolean {
  return comment.reactions.some((reaction) => isExistingReactionOnComment(reaction, teammateId, emojiString));
}

export function toggleReactionOnComment(
  comment: CommentModel,
  teammateId: number,
  emojiString: string,
  shouldKeep?: boolean,
): CommentModel {
  const {reactions} = comment;
  const shouldRemoveReaction = find(reactions, (reaction) =>
    isExistingReactionOnComment(reaction, teammateId, emojiString),
  );

  if (shouldRemoveReaction && shouldKeep) {
    return comment;
  }

  const existingEmojiGroup = find(reactions, (reaction) => reaction.emojiString === emojiString);

  return {
    ...comment,
    reactions: shouldRemoveReaction
      ? reactions.filter((reaction) => !isExistingReactionOnComment(reaction, teammateId, emojiString))
      : [
          ...reactions,
          {
            createdAt: Date.now(),
            teammateId,
            groupCreatedAt: existingEmojiGroup ? existingEmojiGroup.groupCreatedAt : Date.now(),
            emojiString,
          },
        ],
  };
}

function isExistingReactionOnComment(
  reaction: CommentReactionModel,
  teammateId: number,
  emojiString: string,
): boolean {
  return reaction.teammateId === teammateId && reaction.emojiString === emojiString;
}
